import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { filter, takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';

import moment from 'moment';

import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { PhxConstants } from '@common/model';
import { PhxSideBarService } from '@common/services/phx-side-bar.service';

import { 
  InvoiceValidation, 
  WorkOrderPeriodValidation, 
  TimesheetValidationStatus,
  TransactionTypeCount,
  ValidationItem,
  WorkOrderValidationItem,
  ValidationStatus,
  ValidationItemType,
  WorkOrderStatus,
  getValidationIcon,
  getValidationCssClass
} from '../../models';
import { InvoiceDataService } from '../../services/invoice-data.service';

export enum TimesheetFilter {
  All = 'all',
  Unavailable = 'unavailable', 
  Missing = 'missing'
}

@Component({
  selector: 'app-invoice-validation-checklist',
  templateUrl: './invoice-validation-checklist.component.html',
  styleUrls: ['./invoice-validation-checklist.component.less'],
  standalone: false
})
export class InvoiceValidationChecklistComponent extends BaseComponentOnDestroy implements OnInit {
  
  currentInvoiceId: number | null = null;
  private lastLoadedId: number | null = null; // Track last loaded ID to prevent duplicates

  loading = false;
  workOrderValidations: WorkOrderValidationItem[] = [];
  private originalResponse: InvoiceValidation | null = null; // Store original data for filtering
  
  // Filter state
  currentFilter: TimesheetFilter = TimesheetFilter.All;
  TimesheetFilter = TimesheetFilter; // Make enum available to template
  
  // Helper properties for template
  get hasOriginalData(): boolean {
    return !!this.originalResponse && this.originalResponse.WorkOrderPeriods.length > 0;
  }
  
  get isFiltered(): boolean {
    return this.currentFilter !== TimesheetFilter.All;
  }
  
  // Overall status
  overallStatus: WorkOrderStatus = WorkOrderStatus.Warning;
  
  constructor(
    private invoiceDataService: InvoiceDataService,
    private phxSideBarService: PhxSideBarService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.initEntityIdListener();
  }

  private initEntityIdListener(): void {
    // Subscribe to entity ID changes from the sidebar service (like built-in panels do)
    this.phxSideBarService.entityIdChange$()
      .pipe(
        distinctUntilChanged(), // Prevent duplicate calls for same ID
        debounceTime(100), // Small debounce to prevent rapid-fire calls
        filter(id => !!id), // Only proceed with valid IDs
        takeUntil(this.isDestroyed$)
      )
      .subscribe(invoiceId => {
        if (this.currentInvoiceId !== invoiceId) {
          this.currentInvoiceId = invoiceId;
          this.loadValidationData();
        }
      });
  }

  private loadValidationData(): void {
    if (!this.currentInvoiceId) {
      return;
    }

    // Prevent loading same data multiple times
    if (this.lastLoadedId === this.currentInvoiceId) {
      return;
    }

    this.loading = true;
    this.invoiceDataService.getInvoiceSidePanelDetails(this.currentInvoiceId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe({
        next: (response: InvoiceValidation) => {
          this.transformApiResponse(response);
          this.lastLoadedId = this.currentInvoiceId; // Mark as loaded
          this.loading = false;
          this.cdr.detectChanges(); // Manually trigger change detection
        },
        error: (error) => {
          console.error('Error loading invoice validation data:', error);
          this.loading = false;
          this.cdr.detectChanges(); // Manually trigger change detection
        }
      });
  }

  private transformApiResponse(response: InvoiceValidation): void {
    this.originalResponse = response; // Store original data
    this.applyFilterToResponse();
    this.calculateOverallStatus();
  }

  public onFilterChange(selectedFilter: TimesheetFilter): void {
    this.currentFilter = selectedFilter;
    this.applyFilterToResponse();
    this.calculateOverallStatus();
    this.cdr.detectChanges();
  }

  private applyFilterToResponse(): void {
    if (!this.originalResponse) {
      return;
    }

    this.workOrderValidations = this.originalResponse.WorkOrderPeriods
      .map((workOrderPeriod) => this.createWorkOrderValidationItem(workOrderPeriod))
      .filter(workOrder => workOrder.items.length > 0); // Only show periods with relevant items
  }

  private createWorkOrderValidationItem(workOrderPeriod: WorkOrderPeriodValidation): WorkOrderValidationItem {
    const items = this.createValidationItems(workOrderPeriod);
    
    return {
      workOrderId: `${workOrderPeriod.WorkOrderNumber} (${this.formatDateRange(workOrderPeriod.PeriodStartDate, workOrderPeriod.PeriodEndDate)})`,
      workerName: workOrderPeriod.WorkerName || $localize`:@@invoice.validation.unknownWorker:Unknown Worker`,
      workerStatus: this.getOverallWorkOrderStatus(workOrderPeriod),
      isExpanded: true,
      items
    };
  }

  private createValidationItems(workOrderPeriod: WorkOrderPeriodValidation): ValidationItem[] {
    const items: ValidationItem[] = [];

    // Add individual timesheet items if timesheets are displayed for this period
    if (workOrderPeriod.DisplayTimesheets) {
      const timesheetItems = this.createFilteredTimesheetItems(workOrderPeriod);
      items.push(...timesheetItems);
    }

    // Add other transaction type items ONLY when showing "All" filter
    if (this.currentFilter === TimesheetFilter.All && workOrderPeriod.OtherTransactionCounts) {
      workOrderPeriod.OtherTransactionCounts.forEach(transactionCount => {
        const transactionItem = this.createTransactionTypeItem(workOrderPeriod, transactionCount);
        if (transactionItem) {
          items.push(transactionItem);
        }
      });
    }

    return items;
  }

  private createFilteredTimesheetItems(workOrderPeriod: WorkOrderPeriodValidation): ValidationItem[] {
    if (workOrderPeriod.TimesheetDetails.length === 0) {
      return [];
    }

    // Filter timesheets based on current filter
    const filteredTimesheets = workOrderPeriod.TimesheetDetails.filter(timesheet => {
      switch (this.currentFilter) {
        case TimesheetFilter.All:
          return true; // Show all timesheets
        case TimesheetFilter.Unavailable:
          return timesheet.StatusId === TimesheetValidationStatus.Unavailable;
        case TimesheetFilter.Missing:
          return timesheet.StatusId === TimesheetValidationStatus.Missing;
        default:
          return true;
      }
    });

    // Create validation items for filtered timesheets
    return filteredTimesheets.map(timesheet => {
      const statusText = this.getTimesheetStatusText(timesheet.StatusId, timesheet.InvoiceId);
      const dateRange = this.formatDateRange(timesheet.StartDate, timesheet.EndDate);
      
      return {
        id: `${workOrderPeriod.WorkOrderId}-timesheet-${timesheet.Id}`,
        type: ValidationItemType.Timesheet,
        text: $localize`:@@invoice.validation.timesheet.individual:Timesheet ${timesheet.Id} (${dateRange}) - ${statusText}`,
        status: this.getValidationStatusFromTimesheetStatus(timesheet.StatusId)
      };
    });
  }



  private getTimesheetStatusText(statusId: number, invoiceId?: number): string {
    switch (statusId) {
      case TimesheetValidationStatus.Available:
        return $localize`:@@invoice.validation.timesheet.available:Available`;
      case TimesheetValidationStatus.Unavailable:
        return invoiceId 
          ? $localize`:@@invoice.validation.timesheet.unavailable.inInvoice:Unavailable (in Invoice ${invoiceId})`
          : $localize`:@@invoice.validation.timesheet.unavailable:Unavailable`;
      case TimesheetValidationStatus.Missing:
        return $localize`:@@invoice.validation.timesheet.missing:Missing`;
      default:
        return $localize`:@@invoice.validation.timesheet.unknown:Unknown`;
    }
  }

  private getValidationStatusFromTimesheetStatus(timesheetStatusId: number): ValidationStatus {
    switch (timesheetStatusId) {
      case TimesheetValidationStatus.Available:
        return ValidationStatus.Valid; // ✅ Green checkmark
      case TimesheetValidationStatus.Unavailable:
        return ValidationStatus.Warning; // ⚠️ Yellow caution
      case TimesheetValidationStatus.Missing:
        return ValidationStatus.Invalid; // ❌ Red cross
      default:
        return ValidationStatus.Info;
    }
  }

  private createTransactionTypeItem(workOrderPeriod: WorkOrderPeriodValidation, transactionCount: TransactionTypeCount): ValidationItem | null {
    if (transactionCount.Count === 0) {
      return null;
    }

    const transactionTypeName = this.getTransactionTypeName(transactionCount.TransactionTypeId);
    const validationItemType = this.getValidationItemType(transactionCount.TransactionTypeId);

    return {
      id: `${workOrderPeriod.WorkOrderId}-${transactionCount.TransactionTypeId}`,
      text: $localize`:@@invoice.validation.transactionCount:${transactionTypeName} ${transactionCount.Count}`,
      status: ValidationStatus.Info, // Non-timesheet transactions are just informational
      type: validationItemType
    };
  }



  private getOverallWorkOrderStatus(workOrderPeriod: WorkOrderPeriodValidation): WorkOrderStatus {
    const items = this.createValidationItems(workOrderPeriod);
    const validationStatuses = items.map(item => item.status);

    return this.getReducedWorkOrderStatus(validationStatuses);
  }

  private getTransactionTypeName(transactionTypeId: number): string {
    // Map transaction type IDs to human-readable names (with colon for display)
    switch (transactionTypeId) {
      case PhxConstants.TransactionType.Manual:
        return $localize`:@@invoice.validation.manual:Manual entries` + ':';
      case PhxConstants.TransactionType.Expense:
        return $localize`:@@invoice.validation.expense:Expenses` + ':';
      case PhxConstants.TransactionType.Advance:
        return $localize`:@@invoice.validation.advance:Advances` + ':';
      case PhxConstants.TransactionType.VmsTimesheet:
        return $localize`:@@invoice.validation.vmsTimesheet:VMS Timesheets` + ':';
      case PhxConstants.TransactionType.Adjustment:
        return $localize`:@@invoice.validation.adjustment:Adjustments` + ':';
      case PhxConstants.TransactionType.VacationPayment:
        return $localize`:@@invoice.validation.vacationPayment:Vacation Payments` + ':';
      case PhxConstants.TransactionType.VmsCommission:
        return $localize`:@@invoice.validation.vmsCommission:VMS Commissions` + ':';
      case PhxConstants.TransactionType.VmsExpense:
        return $localize`:@@invoice.validation.vmsExpense:VMS Expenses` + ':';
      case PhxConstants.TransactionType.VmsFixedPrice:
        return $localize`:@@invoice.validation.vmsFixedPrice:VMS Fixed Price` + ':';
      case PhxConstants.TransactionType.PermanentPlacement:
        return $localize`:@@invoice.validation.permanentPlacement:Permanent Placements` + ':';
      case PhxConstants.TransactionType.VmsAllowance:
        return $localize`:@@invoice.validation.vmsAllowance:VMS Allowances` + ':';
      case PhxConstants.TransactionType.SickPay:
        return $localize`:@@invoice.validation.sickPay:Sick Pay` + ':';
      default:
        return $localize`:@@invoice.validation.other:Other` + ':';
    }
  }

  private getValidationItemType(transactionTypeId: number): ValidationItemType {
    // Map specific transaction types to validation item types
    if (transactionTypeId === PhxConstants.TransactionType.Expense || 
        transactionTypeId === PhxConstants.TransactionType.VmsExpense) {
      return ValidationItemType.Expense;
    } else if (transactionTypeId === PhxConstants.TransactionType.Manual) {
      return ValidationItemType.Manual;
    } else {
      return ValidationItemType.Manual; // Use Manual as default for other transaction types
    }
  }

  private formatDateRange(startDate: string, endDate: string): string {
    // Use moment to parse API dates and avoid timezone conversion issues
    const start = moment(startDate, PhxConstants.DateFormat.API_Date);
    const end = moment(endDate, PhxConstants.DateFormat.API_Date);
    
    // Format using moment - this matches the "Mar 15" format from the screenshot
    const startFormatted = start.format('MMM D');
    const endFormatted = end.format('MMM D, YYYY');
    
    return `${startFormatted} - ${endFormatted}`;
  }

  private calculateOverallStatus(): void {
    if (this.workOrderValidations.length === 0) {
      this.overallStatus = WorkOrderStatus.Warning;
      return;
    }

    const workOrderStatuses = this.workOrderValidations.map(wo => wo.workerStatus);
    this.overallStatus = this.getReducedStatus(workOrderStatuses);
  }

  private getReducedWorkOrderStatus(validationStatuses: ValidationStatus[]): WorkOrderStatus {
    if (validationStatuses.includes(ValidationStatus.Invalid)) {
      return WorkOrderStatus.NonCompliant;
    } else if (validationStatuses.includes(ValidationStatus.Warning)) {
      return WorkOrderStatus.Warning;
    } else {
      return WorkOrderStatus.Compliant;
    }
  }

  private getReducedStatus(workOrderStatuses: WorkOrderStatus[]): WorkOrderStatus {
    if (workOrderStatuses.includes(WorkOrderStatus.NonCompliant)) {
      return WorkOrderStatus.NonCompliant;
    } else if (workOrderStatuses.includes(WorkOrderStatus.Warning)) {
      return WorkOrderStatus.Warning;
    } else {
      return WorkOrderStatus.Compliant;
    }
  }

  getStatusIcon(status: ValidationStatus | WorkOrderStatus): string {
    return getValidationIcon(status);
  }

  getStatusClass(status: ValidationStatus | WorkOrderStatus): string {
    return getValidationCssClass(status);
  }

  trackByWorkOrder(index: number, item: WorkOrderValidationItem): string {
    return item.workOrderId;
  }

  trackByItem(index: number, item: ValidationItem): string {
    return item.id;
  }
}
